import React, { useState } from 'react';
import { Calendar, Info, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import config from '@/config';
import { apiFetch } from '@/utils/api';

interface AgeGroupOnboardingProps {
  isOpen: boolean;
  onComplete: () => void;
}

const AGE_GROUP_OPTIONS = [
  { value: 'under-18', label: 'Under 18', icon: '👶', disabled: true, tooltip: 'Must be 18+ to use this service' },
  { value: '18-24', label: '18-24', icon: '🧑' },
  { value: '25-34', label: '25-34', icon: '👨' },
  { value: '35-44', label: '35-44', icon: '👨‍🦰' },
  { value: '45+', label: '45+', icon: '👴' }
];

export function AgeGroupOnboarding({ isOpen, onComplete }: AgeGroupOnboardingProps) {
  const { getIdToken } = useAuth();
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleAgeGroupSubmit = async () => {
    if (!selectedAgeGroup) {
      setError('Please select an age group to continue');
      return;
    }

    if (selectedAgeGroup === 'under-18') {
      setError('You must be at least 18 years old to use this service');
      return;
    }

    setIsUpdating(true);
    setError(null);
    setSuccess(false);

    try {
      const token = getIdToken();
      if (!token) {
        setError('Authentication required. Please login again.');
        return;
      }

      let age: number = 0;
      switch (selectedAgeGroup) {
        case 'under-18': {
          age = 17;
          break;
        }
        case '18-24': {
          age = 18;
          break;
        }
        case '25-34': {
          age = 25;
          break;
        }
        case '35-44': {
          age = 35;
          break;
        }
        case '45+': {
          age = 45;
          break;
        }
      }

      const response = await apiFetch(`${config.baseUrl}/${config.endpoints.updateAgeGroup}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ age: age })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to set age group. Please try again.');
        return;
      }

      // Success
      setSuccess(true);
      setTimeout(() => {
        // Refresh to get updated user data
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Error setting age group:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="bg-[#1C2431] border border-slate-700 text-white sm:max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Calendar className="w-6 h-6 text-purple-400" />
            <DialogTitle className="text-white text-xl">One More Step!</DialogTitle>
          </div>
          <DialogDescription className="text-slate-300 text-center">
            Please select your age group to complete your profile.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Age Group Selection */}
          <div>
            <label className="text-white text-sm font-medium mb-3 block text-center">
              Select Your Age Group
            </label>
            <div className="grid grid-cols-2 gap-3">
              {AGE_GROUP_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    if (!option.disabled) {
                      setSelectedAgeGroup(option.value);
                      setError(null);
                      setSuccess(false);
                    } else {
                      setError(option.tooltip || 'This option is not available');
                    }
                  }}
                  disabled={isUpdating || success}
                  className={`p-4 rounded-lg text-sm font-medium transition-all border-2 ${
                    selectedAgeGroup === option.value
                      ? 'bg-purple-600 border-purple-500 text-white shadow-lg shadow-purple-500/50'
                      : option.disabled
                      ? 'bg-[#0E1525] border-red-600 text-slate-500 cursor-not-allowed opacity-50'
                      : 'bg-[#0E1525] border-slate-600 text-slate-300 hover:bg-[#1C2431] hover:border-purple-400 hover:text-white'
                  } ${(isUpdating || success) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={option.disabled ? option.tooltip : undefined}
                >
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="text-2xl">{option.icon}</span>
                    <span>{option.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Info Message */}
          <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-slate-300 text-xs">
                You can update your age group later in settings, but changes are limited to once per year.
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                <p className="text-green-300 text-sm">Age group set successfully! Redirecting...</p>
              </div>
            </div>
          )}

          {/* Continue Button */}
          <Button
            onClick={handleAgeGroupSubmit}
            disabled={!selectedAgeGroup || selectedAgeGroup === 'under-18' || isUpdating || success}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed py-6 text-base font-semibold"
          >
            {isUpdating ? 'Setting Age Group...' : success ? 'Redirecting...' : 'Continue to Video Chat'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
